import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Download, LoaderCircle } from "lucide-react";
import type {
  ContactImportReportListItem,
  ContactImportReportListResponse,
  ContactImportReportPageSize,
} from "@multi-chat/shared";
import { CONTACT_IMPORT_REPORT_PAGE_SIZES } from "@multi-chat/shared";
import { isApiError } from "../../api/client.js";
import { LtrValue } from "../../components/common/LtrValue.js";
import { Button } from "../../components/ui/button/index.js";
import { cn } from "../../lib/utils.js";
import { buildContactImportReportDownloadPath } from "../../api/reports-api.js";
import { showingCampaignRangeBounds } from "./campaign-report-presentation.js";
import { formatReportDateTime } from "./outgoing-report-presentation.js";
import { reportColumnHeaderDragHandlers } from "./report-column-header-drag.js";
import { ReportColumnResizeHandle } from "./ReportColumnResizeHandle.js";
import { ReportPaginationBar } from "./ReportPaginationBar.js";
import { ReportResultState } from "./ReportResultState.js";
import type { ReportEmptyKind } from "./report-empty-kind.js";
import type { ContactImportReportColumnId } from "./report-column-defs.js";
import {
  contactsTableHeaderLabelClass,
  reportColumnStyle,
  reportTableBodyCellClass,
  reportTableBodyRowClass,
  reportTableClass,
  reportTableHeaderCellClass,
  reportTableHeadRowClass,
  reportTableScrollClass,
  reportTableWorkspaceClass,
} from "./report-table-presentation.js";
import { downloadReportExport } from "./report-export-download.js";
import { exportLocaleFromUi } from "./use-report-export.js";

const NUMERIC_COLUMNS = new Set<ContactImportReportColumnId>([
  "rows",
  "created",
  "restored",
  "skipped",
  "failed",
  "warnings",
]);

function columnLabel(t: (key: string) => string, id: ContactImportReportColumnId): string {
  switch (id) {
    case "file":
    case "date":
    case "rows":
    case "created":
    case "restored":
    case "skipped":
    case "failed":
    case "warnings":
    case "report":
      return t(`reports.contactImports.columns.${id}`);
    default: {
      const _exhaustive: never = id;
      return _exhaustive;
    }
  }
}

export function ContactImportReportTable({
  organizationId,
  list,
  loading,
  page,
  pageSize,
  emptyKind,
  visibleColumns,
  widths,
  onResizeColumn,
  onReorderColumn,
  onPageChange,
  onPageSizeChange,
  onResetFilters,
}: {
  organizationId: number;
  list: ContactImportReportListResponse | null;
  loading: boolean;
  page: number;
  pageSize: number;
  emptyKind: ReportEmptyKind;
  visibleColumns: readonly ContactImportReportColumnId[];
  widths: Readonly<Record<ContactImportReportColumnId, number>>;
  onResizeColumn: (id: ContactImportReportColumnId, width: number) => void;
  onReorderColumn: (fromId: ContactImportReportColumnId, toId: ContactImportReportColumnId) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: ContactImportReportPageSize) => void;
  onResetFilters: () => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const items = list?.items ?? [];
  const total = list?.totalCount ?? 0;
  const totalPages = list?.totalPages ?? 0;
  const { start, end } = showingCampaignRangeBounds(page, pageSize, total);
  const colCount = Math.max(visibleColumns.length, 1);
  const dragFromRef = useRef<ContactImportReportColumnId | null>(null);
  const resizeRef = useRef<{
    id: ContactImportReportColumnId;
    startX: number;
    startWidth: number;
    rtl: boolean;
  } | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  useEffect(() => {
    const onMove = (event: PointerEvent) => {
      const active = resizeRef.current;
      if (active == null) return;
      const delta = active.rtl ? active.startX - event.clientX : event.clientX - active.startX;
      onResizeColumn(active.id, active.startWidth + delta);
    };
    const onUp = () => {
      resizeRef.current = null;
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [onResizeColumn]);

  const onDownload = async (row: ContactImportReportListItem) => {
    if (row.reportAvailability !== "available" || downloadingId != null) return;
    setDownloadingId(row.id);
    setDownloadError(null);
    try {
      await downloadReportExport({
        pathWithQuery: buildContactImportReportDownloadPath(
          organizationId,
          row.id,
          exportLocaleFromUi(i18n.language),
        ),
        fallbackFilename: "import-report.csv",
      });
    } catch (error) {
      setDownloadError(
        isApiError(error) && error.status === 410
          ? t("reports.contactImports.expired")
          : t("reports.contactImports.downloadFailed"),
      );
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className={reportTableWorkspaceClass} data-testid="reports-contact-imports-table">
      <div className={reportTableScrollClass}>
        <table className={reportTableClass} style={{ tableLayout: "fixed", width: "max-content", minWidth: "100%" }}>
          <thead>
            <tr className={reportTableHeadRowClass}>
              {visibleColumns.map((columnId) => {
                const label = columnLabel(t, columnId);
                return (
                  <th
                    key={columnId}
                    className={cn(reportTableHeaderCellClass, "text-center")}
                    style={reportColumnStyle(widths[columnId])}
                    {...reportColumnHeaderDragHandlers(columnId, dragFromRef, onReorderColumn)}
                    data-testid={`reports-contact-imports-column-${columnId}`}
                  >
                    <span className={cn(contactsTableHeaderLabelClass, "w-full justify-center")}>
                      {label}
                    </span>
                    <ReportColumnResizeHandle
                      label={t("reports.chrome.resizeColumn", { column: label })}
                      onResizeStart={(clientX) => {
                        const rtl =
                          typeof document !== "undefined" &&
                          getComputedStyle(document.documentElement).direction === "rtl";
                        resizeRef.current = {
                          id: columnId,
                          startX: clientX,
                          startWidth: widths[columnId],
                          rtl,
                        };
                      }}
                    />
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading && items.length === 0 ? (
              Array.from({ length: 6 }, (_, index) => (
                <tr key={index} className={reportTableBodyRowClass}>
                  <td colSpan={colCount} className={reportTableBodyCellClass}>
                    <div className="h-4 w-full animate-pulse rounded bg-surface-muted motion-reduce:animate-none" />
                  </td>
                </tr>
              ))
            ) : items.length === 0 ? (
              <tr className={reportTableBodyRowClass}>
                <td colSpan={colCount} className={cn(reportTableBodyCellClass, "px-4 py-10 text-center")}>
                  <ReportResultState
                    kind={emptyKind}
                    i18nPrefix="reports.contactImports"
                    onResetFilters={onResetFilters}
                    testId="reports-contact-imports-empty"
                  />
                </td>
              </tr>
            ) : (
              items.map((row) => (
                <ContactImportReportRowView
                  key={row.id}
                  row={row}
                  locale={i18n.language}
                  visibleColumns={visibleColumns}
                  widths={widths}
                  downloading={downloadingId === row.id}
                  onDownload={() => void onDownload(row)}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
      {downloadError ? (
        <p className="m-0 px-4 py-2 text-[0.75rem] text-danger" role="alert">
          {downloadError}
        </p>
      ) : null}
      <ReportPaginationBar
        page={page}
        pageSize={pageSize}
        totalCount={total}
        totalPages={Math.max(totalPages, 1)}
        pageSizes={CONTACT_IMPORT_REPORT_PAGE_SIZES}
        onPageChange={onPageChange}
        onPageSizeChange={(size) => onPageSizeChange(size as ContactImportReportPageSize)}
        showingLabel={t("reports.contactImports.showingRange")}
        pageOfLabel={t("reports.contactImports.pageOf")}
        prevLabel={t("reports.contactImports.prev")}
        nextLabel={t("reports.contactImports.next")}
        pageSizeLabel={t("reports.contactImports.pageSize")}
        testIdPrefix="reports-contact-imports"
        rangeStart={start}
        rangeEnd={end}
      />
    </div>
  );
}

function ContactImportReportRowView({
  row,
  locale,
  visibleColumns,
  widths,
  downloading,
  onDownload,
}: {
  row: ContactImportReportListItem;
  locale: string;
  visibleColumns: readonly ContactImportReportColumnId[];
  widths: Readonly<Record<ContactImportReportColumnId, number>>;
  downloading: boolean;
  onDownload: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <tr className={reportTableBodyRowClass} data-testid={`reports-contact-imports-row-${row.id}`}>
      {visibleColumns.map((columnId) => (
        <td
          key={columnId}
          className={cn(
            reportTableBodyCellClass,
            "text-center",
            NUMERIC_COLUMNS.has(columnId) && "tabular-nums",
          )}
          style={reportColumnStyle(widths[columnId])}
        >
          {renderCell(t, row, columnId, locale, downloading, onDownload)}
        </td>
      ))}
    </tr>
  );
}

function renderCell(
  t: (key: string) => string,
  row: ContactImportReportListItem,
  columnId: ContactImportReportColumnId,
  locale: string,
  downloading: boolean,
  onDownload: () => void,
): React.ReactNode {
  switch (columnId) {
    case "file":
      return (
        <span className="block truncate" title={row.originalFileName}>
          {row.originalFileName}
        </span>
      );
    case "date":
      return (
        <LtrValue className="tabular-nums text-[0.75rem]">
          {formatReportDateTime(row.completedAt, locale)}
        </LtrValue>
      );
    case "rows":
      return row.totalRows;
    case "created":
      return row.createdCount;
    case "restored":
      return row.restoredCount;
    case "skipped":
      return row.skippedCount;
    case "failed":
      return row.failedCount;
    case "warnings":
      return row.warningCount;
    case "report":
      return <ReportDownloadCell row={row} downloading={downloading} onDownload={onDownload} t={t} />;
    default: {
      const _exhaustive: never = columnId;
      return _exhaustive;
    }
  }
}

function ReportDownloadCell({
  row,
  downloading,
  onDownload,
  t,
}: {
  row: ContactImportReportListItem;
  downloading: boolean;
  onDownload: () => void;
  t: (key: string) => string;
}): React.JSX.Element {
  if (row.reportAvailability === "available") {
    const label = downloading
      ? t("reports.contactImports.downloading")
      : t("reports.contactImports.download");
    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={downloading}
        aria-label={label}
        title={label}
        className="size-8 p-0"
        data-testid={`reports-contact-imports-download-${row.id}`}
        onClick={onDownload}
      >
        {downloading ? (
          <LoaderCircle size={14} className="animate-spin motion-reduce:animate-none" aria-hidden="true" />
        ) : (
          <Download size={14} aria-hidden="true" />
        )}
      </Button>
    );
  }
  const message =
    row.reportAvailability === "expired"
      ? t("reports.contactImports.expired")
      : t("reports.contactImports.unavailable");
  return (
    <p className="m-0 text-center text-[0.75rem] leading-snug text-muted" data-testid={`reports-contact-imports-report-${row.id}`}>
      {message}
    </p>
  );
}
